import * as THREE from "three";
import type { ActiveImpact, SampledEntity, Vec3 } from "../core";
import { inferActionMotionIntent, sampleActionMotionPolicy, type RotationChannels } from "../motion";
import type { SecondaryResponse } from "../physics";
import { COURT_WARRIOR_ITEMS, equipHumanoid } from "./humanoid-equipment";
import { applyActionFootPlacement, applyFootTargets, applyTwoBoneTarget } from "./humanoid-solver";

export type CharacterMotionState = "idle" | "locomotion" | "action" | "hit" | "defeated";

export interface CharacterMotionSample {
  state: CharacterMotionState;
  progress: number;
}

export interface CharacterImpactSample {
  /** Unit impulse direction in actor-local coordinates. */
  direction: Vec3;
  strength: number;
  pointHeight: number;
}

export interface CharacterRigMaterials {
  body: THREE.MeshToonMaterial;
  accent: THREE.MeshToonMaterial;
  dark: THREE.MeshToonMaterial;
}

export interface SecondaryBodyBinding {
  id: string;
  object: THREE.Object3D;
  response: SecondaryResponse;
  /** Static attachment rest rotation; animated joints instead add to their pose. */
  restRotation: Vec3;
  additiveToPose: boolean;
  rotationScale: number;
}

export interface CharacterRig {
  modelRoot: THREE.Group;
  torso: THREE.Group;
  head: THREE.Group;
  leftArm: THREE.Group;
  rightArm: THREE.Group;
  leftForearm: THREE.Group;
  rightForearm: THREE.Group;
  leftHand: THREE.Group;
  rightHand: THREE.Group;
  leftLeg: THREE.Group;
  rightLeg: THREE.Group;
  leftShin: THREE.Group;
  rightShin: THREE.Group;
  leftFoot: THREE.Group;
  rightFoot: THREE.Group;
  /** Equipment-independent grip pivot below the articulated right wrist. */
  weaponGrip: THREE.Group;
  weapon: THREE.Object3D;
  guard: THREE.Object3D;
  sockets: {
    hud: THREE.Object3D;
    weapon: THREE.Object3D;
    vfx: THREE.Object3D;
    secondaryMotion: THREE.Object3D;
  };
  materials: CharacterRigMaterials;
  secondaryBodies: SecondaryBodyBinding[];
  equippedItems: readonly string[];
  torsoBaseY: number;
  defaultGuardVisible: boolean;
}

export interface CharacterPoseContext {
  entity: SampledEntity;
  motion: CharacterMotionSample;
  time: number;
  exaggeration: number;
  impact?: CharacterImpactSample;
}

export interface ClassPresentationKit {
  id: string;
  label: string;
  create(entity: SampledEntity, materials: CharacterRigMaterials): CharacterRig;
  pose(rig: CharacterRig, context: CharacterPoseContext): void;
}

const FALLBACK_KIT_ID = "fallback.actor.v1";
export const COURT_WARRIOR_KIT_ID = "court.warrior.v1";

/**
 * Shared animation skeleton proportions, expressed in model-local scene units.
 * The unarmored figure is 7.5 head-heights tall; class gear may extend beyond
 * that contour without changing the skeleton or gameplay footprint.
 */
export const HUMANOID_PROPORTIONS = Object.freeze({
  figureHeight: 2.85,
  headDiameter: 0.38,
  headCenterY: 2.66,
  shoulderY: 2.22,
  shoulderX: 0.43,
  torsoCenterY: 1.76,
  torsoHeight: 0.88,
  hipY: 1.26,
  hipX: 0.16,
  upperArmLength: 0.49,
  lowerArmLength: 0.47,
  upperLegLength: 0.63,
  lowerLegLength: 0.60,
});

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

export function resolveCharacterMotion(entity: SampledEntity, impacts: ActiveImpact[]): CharacterMotionSample {
  if ((entity.vitals?.health ?? 1) <= 0 || entity.statuses.includes("defeated")) {
    return { state: "defeated", progress: 1 };
  }

  const incoming = resolveIncomingImpact(entity, impacts);
  if (incoming && incoming.age <= 0.34) {
    return { state: "hit", progress: clamp01(incoming.age / 0.34) };
  }

  if (entity.action) {
    return { state: "action", progress: clamp01(entity.action.totalProgress) };
  }

  // Court's instant auto attacks do not currently expose a cast phase. The
  // adapter's outcome-authoritative impact and staging-associated source ID
  // still provide a short presentation trigger without predicting a result.
  const outgoing = impacts
    .filter((impact) => impact.sourceId === entity.id && impact.outcome !== "heal" && impact.outcome !== "miss")
    .sort((a, b) => a.age - b.age)[0];
  if (outgoing && outgoing.age <= 0.28) {
    return { state: "action", progress: clamp01(outgoing.age / 0.28) };
  }

  if (entity.movement) return { state: "locomotion", progress: entity.movement.progress };
  return { state: "idle", progress: 0 };
}

export function resolveIncomingImpact(entity: SampledEntity, impacts: ActiveImpact[]): ActiveImpact | undefined {
  return impacts
    .filter((impact) => impact.targetId === entity.id && impact.outcome === "hit")
    .sort((a, b) => a.age - b.age)[0];
}

interface JointedLimb {
  root: THREE.Group;
  mid: THREE.Group;
  end: THREE.Group;
}

function createJointedLimb(
  parent: THREE.Group,
  x: number,
  y: number,
  material: THREE.Material,
  upperLength: number,
  lowerLength: number,
  upperRadius: number,
  lowerRadius: number,
  endKind: "hand" | "foot",
  endMaterial: THREE.Material,
): JointedLimb {
  const root = new THREE.Group();
  root.name = endKind === "hand" ? "joint.shoulder" : "joint.hip";
  root.position.set(x, y, 0);
  const upper = new THREE.Mesh(new THREE.CylinderGeometry(upperRadius * 0.82, upperRadius, upperLength, 6), material);
  upper.position.y = -upperLength / 2;
  root.add(upper);

  const mid = new THREE.Group();
  mid.name = endKind === "hand" ? "joint.elbow" : "joint.knee";
  mid.position.y = -upperLength;
  const joint = new THREE.Mesh(new THREE.IcosahedronGeometry(lowerRadius * 1.18, 0), material);
  mid.add(joint);
  const lower = new THREE.Mesh(new THREE.CylinderGeometry(lowerRadius * 0.78, lowerRadius, lowerLength, 6), material);
  lower.position.y = -lowerLength / 2;
  mid.add(lower);
  root.add(mid);

  const end = new THREE.Group();
  end.name = endKind === "hand" ? "joint.wrist" : "joint.ankle";
  end.position.y = -lowerLength;
  if (endKind === "hand") {
    // Keep a small readable break between the forearm and palm. At the target
    // pixel scale this joint is what makes wrist flexion visible instead of
    // merging the arm, hand, and weapon into one long stick.
    const wrist = new THREE.Mesh(new THREE.IcosahedronGeometry(lowerRadius * 0.72, 0), material);
    wrist.name = "joint.wrist.visual";
    end.add(wrist);
  }
  const endMesh = endKind === "hand"
    ? new THREE.Mesh(new THREE.BoxGeometry(lowerRadius * 1.65, lowerRadius * 1.9, lowerRadius * 1.45), endMaterial)
    : new THREE.Mesh(new THREE.BoxGeometry(lowerRadius * 1.9, lowerRadius * 1.25, lowerRadius * 3.1), endMaterial);
  if (endKind === "foot") endMesh.position.set(0, -lowerRadius * 0.28, lowerRadius * 0.72);
  else endMesh.position.y = -lowerRadius * 0.82;
  end.add(endMesh);
  mid.add(end);
  parent.add(root);
  return { root, mid, end };
}

function socket(name: string, parent: THREE.Object3D, x: number, y: number, z: number): THREE.Group {
  const point = new THREE.Group();
  point.name = name;
  point.position.set(x, y, z);
  parent.add(point);
  return point;
}

function resetRig(rig: CharacterRig): void {
  rig.modelRoot.position.set(0, 0, 0);
  rig.modelRoot.rotation.set(0, 0, 0);
  rig.torso.position.y = rig.torsoBaseY;
  rig.torso.rotation.set(0, 0, 0);
  rig.head.rotation.set(0, 0, 0);
  rig.leftArm.rotation.set(0, 0, 0);
  rig.rightArm.rotation.set(0, 0, 0);
  rig.leftForearm.rotation.set(0, 0, 0);
  rig.rightForearm.rotation.set(0, 0, 0);
  rig.leftHand.rotation.set(0, 0, 0);
  rig.rightHand.rotation.set(0, 0, 0);
  rig.leftLeg.rotation.set(0, 0, 0);
  rig.rightLeg.rotation.set(0, 0, 0);
  rig.leftShin.rotation.set(0, 0, 0);
  rig.rightShin.rotation.set(0, 0, 0);
  rig.leftFoot.rotation.set(0, 0, 0);
  rig.rightFoot.rotation.set(0, 0, 0);
  rig.weaponGrip.rotation.set(-0.16, 0, 0.08);
  rig.weapon.rotation.set(0, 0, 0);
  rig.guard.rotation.set(0, 0, 0);
  rig.weapon.visible = true;
  rig.guard.visible = rig.defaultGuardVisible;
}

function poseAction(rig: CharacterRig, context: CharacterPoseContext): void {
  const action = context.entity.action;
  const instant = instantAttackPhase(context.motion.progress);
  const pose = sampleActionMotionPolicy(
    inferActionMotionIntent(action),
    action?.phase ?? instant.phase,
    action?.phaseProgress ?? instant.progress,
    action?.transitionFrom,
  );
  const amount = context.exaggeration;
  rig.modelRoot.position.set(pose.root.x * amount, pose.root.y * amount, pose.root.z * amount);
  applyRotation(rig.torso, pose.torso, amount);
  applyRotation(rig.head, pose.head, amount);
  applyRotation(rig.leftArm, pose.leftArm, amount);
  applyRotation(rig.rightArm, pose.rightArm, amount);
  applyRotation(rig.leftForearm, pose.leftForearm, amount);
  applyRotation(rig.rightForearm, pose.rightForearm, amount);
  applyRotation(rig.leftHand, pose.leftHand, amount);
  applyRotation(rig.rightHand, pose.rightHand, amount);
  applyRotation(rig.leftLeg, pose.leftLeg, amount);
  applyRotation(rig.rightLeg, pose.rightLeg, amount);
  applyRotation(rig.leftShin, pose.leftShin, amount);
  applyRotation(rig.rightShin, pose.rightShin, amount);
  applyRotation(rig.leftFoot, pose.leftFoot, amount);
  applyRotation(rig.rightFoot, pose.rightFoot, amount);
  applyRotation(rig.weaponGrip, pose.weapon, amount);
  applyRotation(rig.guard, pose.guard, amount);
  applyActionFootPlacement(rig, pose, HUMANOID_PROPORTIONS, amount);
  applyActionArmPlacement(rig, pose, inferActionMotionIntent(action));
}

/**
 * Resolve the active arm from a wrist target, then aim the held equipment at a
 * separate endpoint target. This is the golden-character seam: authored pose
 * channels still provide the telegraph, torso, passive arm, and wrist detail,
 * while reach/contact is enforced by one analytic solver for every preset.
 */
function applyActionArmPlacement(
  rig: CharacterRig,
  pose: ReturnType<typeof sampleActionMotionPolicy>,
  intent: ReturnType<typeof inferActionMotionIntent>,
): void {
  const active = intent.hand === "right"
    ? { root: rig.rightArm, mid: rig.rightForearm, end: rig.rightHand }
    : { root: rig.leftArm, mid: rig.leftForearm, end: rig.leftHand };
  applyTwoBoneTarget(
    active.root,
    active.mid,
    pose.activeHandTarget,
    HUMANOID_PROPORTIONS.upperArmLength,
    HUMANOID_PROPORTIONS.lowerArmLength,
    { x: 0, y: 0, z: 1 },
  );

  // The weapon and shield are deliberately aimed after the arm solve. Their
  // endpoint trajectory can exceed the hand reach without pulling the wrist
  // beyond its anatomical envelope, and the separate grip remains visible.
  rig.modelRoot.updateMatrixWorld(true);
  if (intent.hand === "right" && intent.delivery !== "bash") {
    aimLocalChildAxis(
      rig.weaponGrip,
      rig.sockets.weapon,
      pose.weaponEndpointTarget,
      new THREE.Vector3(0, -1, 0),
      rig.modelRoot,
    );
  } else if (intent.delivery === "bash") {
    aimLocalChildAxis(
      rig.guard,
      rig.guard,
      pose.weaponEndpointTarget,
      new THREE.Vector3(1, 0, 0),
      rig.modelRoot,
    );
  }

  // Motion emphasis already scales authored channels and root motion before
  // this constraint pass. Keep the solved target exact; partially scaling the
  // root after foot placement would reintroduce the very skating this solve
  // exists to prevent.
}

function aimLocalChildAxis(
  object: THREE.Object3D,
  origin: THREE.Object3D,
  target: Vec3,
  localAxis: THREE.Vector3,
  modelRoot: THREE.Object3D,
): void {
  const originWorld = origin.getWorldPosition(new THREE.Vector3());
  const targetWorld = new THREE.Vector3(target.x, target.y, target.z).applyMatrix4(modelRoot.matrixWorld);
  const direction = targetWorld.sub(originWorld);
  if (direction.lengthSq() < 1e-8) return;
  const desiredWorld = new THREE.Quaternion().setFromUnitVectors(localAxis, direction.normalize());
  const parentWorld = object.parent?.getWorldQuaternion(new THREE.Quaternion()) ?? new THREE.Quaternion();
  object.quaternion.copy(parentWorld.invert().multiply(desiredWorld));
}

function applyRotation(object: THREE.Object3D, rotation: RotationChannels, amount: number): void {
  object.rotation.set(rotation.x * amount, rotation.y * amount, rotation.z * amount);
}

function instantAttackPhase(progress: number): { phase: "commit" | "impact" | "recover"; progress: number } {
  const p = clamp01(progress);
  if (p < 0.36) return { phase: "commit", progress: p / 0.36 };
  if (p < 0.58) return { phase: "impact", progress: (p - 0.36) / 0.22 };
  return { phase: "recover", progress: (p - 0.58) / 0.42 };
}

function poseImpactResponse(rig: CharacterRig, context: CharacterPoseContext, warrior: boolean): void {
  const envelope = Math.sin(clamp01(context.motion.progress) * Math.PI)
    * context.exaggeration
    * (context.impact?.strength ?? 0.72);
  const direction = normalizedDirection(context.impact?.direction ?? { x: 0.42, y: 0, z: -0.91 });
  const height = context.impact?.pointHeight ?? 0.58;

  // Translate with the impulse, then let the upper body lag around the planted
  // stance. X rotation leans along local Z; negative Z rotation leans +X.
  rig.modelRoot.position.x = direction.x * 0.11 * envelope;
  rig.modelRoot.position.z = direction.z * 0.11 * envelope;
  rig.torso.rotation.x = direction.z * (0.26 + height * 0.18) * envelope;
  rig.torso.rotation.z = -direction.x * (0.26 + height * 0.18) * envelope;
  rig.head.rotation.x = -direction.z * 0.16 * envelope;
  rig.head.rotation.z = direction.x * 0.16 * envelope;
  rig.leftArm.rotation.x = (warrior ? -0.82 : 0) - direction.z * 0.28 * envelope;
  rig.rightArm.rotation.x = -direction.z * 0.46 * envelope;
  rig.leftArm.rotation.z = (warrior ? -0.28 : 0) + direction.x * 0.22 * envelope;
  rig.rightArm.rotation.z = direction.x * 0.34 * envelope;
  rig.leftForearm.rotation.x = (warrior ? -1.02 : -0.38) + 0.16 * envelope;
  rig.rightForearm.rotation.x = -0.32 - 0.3 * envelope;
  rig.leftShin.rotation.x = Math.max(0, -direction.z) * 0.18 * envelope;
  rig.rightShin.rotation.x = Math.max(0, direction.z) * 0.18 * envelope;
}

function normalizedDirection(direction: Vec3): Vec3 {
  const length = Math.hypot(direction.x, direction.y, direction.z);
  if (length < 1e-5) return { x: 0, y: 0, z: -1 };
  return { x: direction.x / length, y: direction.y / length, z: direction.z / length };
}

/** Shared velocity-weighted gait used by every humanoid kit. */
function poseLocomotion(rig: CharacterRig, context: CharacterPoseContext, warrior: boolean): void {
  const { entity, motion: sample, exaggeration } = context;
  const movement = entity.movement;
  const intensity = clamp01(movement?.intensity ?? 1);
  const cadence = movement?.gait === "dash" ? 22 : movement?.gait === "run" ? 14 : 9;
  const phase = sample.progress * cadence;
  const strideWave = Math.sin(phase);
  const doubleStep = Math.sin(phase * 2);
  const stride = strideWave * (0.26 + intensity * 0.54) * exaggeration;
  const lift = Math.abs(strideWave) * intensity;
  const velocity = movement?.velocity;
  const speed = velocity ? Math.hypot(velocity.x, velocity.z) : 0;
  const yaw = entity.rotation?.y ?? 0;
  const localStrafe = speed > 1e-4
    ? (Math.cos(yaw) * velocity!.x - Math.sin(yaw) * velocity!.z) / speed
    : 0;
  const localForward = speed > 1e-4
    ? (Math.sin(yaw) * velocity!.x + Math.cos(yaw) * velocity!.z) / speed
    : 1;
  const footTargets = locomotionFootTargets(sample.progress, cadence, intensity, localStrafe, localForward, movement?.gait ?? "run");

  rig.modelRoot.position.x = doubleStep * 0.022 * intensity * exaggeration;
  rig.modelRoot.rotation.z = -localStrafe * 0.07 * intensity + doubleStep * 0.018 * intensity;
  rig.torso.position.y += lift * 0.075 * exaggeration;
  rig.torso.rotation.x = -(0.025 + intensity * (movement?.gait === "dash" ? 0.16 : 0.075));
  rig.torso.rotation.y = stride * 0.11;
  rig.torso.rotation.z = -localStrafe * 0.09 * intensity - doubleStep * 0.035 * intensity;
  rig.head.rotation.x = -rig.torso.rotation.x * 0.48;
  rig.head.rotation.y = -rig.torso.rotation.y * 0.58;
  rig.head.rotation.z = -rig.torso.rotation.z * 0.42;

  if (warrior) {
    rig.leftArm.rotation.x = -0.68 - stride * 0.16;
    rig.leftArm.rotation.z = -0.28 + doubleStep * 0.025;
    rig.leftForearm.rotation.x = -1.04 + lift * 0.08;
    rig.rightArm.rotation.x = stride * 0.58;
    rig.rightArm.rotation.z = -0.12 - localStrafe * 0.04;
    rig.rightForearm.rotation.x = -0.3 - Math.max(0, -stride) * 0.3;
    rig.weaponGrip.rotation.set(-0.16 + stride * 0.06, 0, 0.08 + stride * 0.08);
  } else {
    rig.leftArm.rotation.x = -stride * 0.72;
    rig.rightArm.rotation.x = stride * 0.72;
    rig.leftArm.rotation.z = doubleStep * 0.035;
    rig.rightArm.rotation.z = -doubleStep * 0.035;
    rig.leftForearm.rotation.x = -0.24 - Math.max(0, stride) * 0.28;
    rig.rightForearm.rotation.x = -0.24 - Math.max(0, -stride) * 0.28;
  }

  // Foot targets are phase-derived rather than frame-history-derived. The
  // raised half-wave is the swing; the extrema are deterministic stance
  // windows where the ankle remains planted while the body passes over it.
  applyFootTargets(rig, footTargets.left, footTargets.right, { x: rig.modelRoot.position.x, y: rig.modelRoot.position.y, z: rig.modelRoot.position.z }, HUMANOID_PROPORTIONS, 1);
}

export function locomotionFootTargets(
  progress: number,
  cadence: number,
  intensity: number,
  localStrafe: number,
  localForward: number,
  gait: "walk" | "run" | "dash",
): { left: Vec3; right: Vec3 } {
  const stride = (gait === "dash" ? 0.26 : gait === "run" ? 0.21 : 0.13) * clamp01(intensity);
  const lateral = localStrafe * stride * 0.36;
  const forward = Math.max(0.2, Math.abs(localForward)) * stride;
  const cycle = progress * cadence;
  return {
    left: gaitFootTarget(-1, cycle, stride, lateral, forward),
    right: gaitFootTarget(1, cycle + 0.5, stride, lateral, forward),
  };
}

function gaitFootTarget(
  side: -1 | 1,
  phaseInput: number,
  stride: number,
  lateral: number,
  forward: number,
): Vec3 {
  const phase = phaseInput - Math.floor(phaseInput);
  const wave = Math.sin(phase * Math.PI * 2);
  const lift = Math.max(0, wave) ** 1.35;
  const stanceOffset = -Math.cos(phase * Math.PI * 2) * forward;
  return {
    x: side * 0.16 + lateral * wave,
    y: 0.09 + lift * (0.075 + stride * 0.18),
    z: stanceOffset,
  };
}

function poseFallback(rig: CharacterRig, context: CharacterPoseContext): void {
  resetRig(rig);
  const { entity, motion: sample, exaggeration, time } = context;
  if (sample.state === "defeated") {
    rig.modelRoot.rotation.z = -1.18;
    rig.modelRoot.position.y = 0.18;
    rig.rightArm.rotation.z = -0.6;
    rig.rightForearm.rotation.x = -0.8;
    rig.leftShin.rotation.x = 0.55;
    return;
  }
  if (sample.state === "hit") {
    poseImpactResponse(rig, context, false);
    return;
  }
  if (sample.state === "action") {
    poseAction(rig, context);
    return;
  }
  if (sample.state === "locomotion") {
    poseLocomotion(rig, context, false);
    return;
  }
  rig.modelRoot.position.y = Math.sin(time * 2.2) * 0.018;
  rig.torso.rotation.z = Math.sin(time * 1.4) * 0.025;
  rig.head.rotation.y = Math.sin(time * 0.8) * 0.09;
  rig.leftForearm.rotation.x = -0.22;
  rig.rightForearm.rotation.x = -0.18;
  rig.leftShin.rotation.x = 0.05;
  rig.rightShin.rotation.x = 0.05;
}

function createHumanoidBase(materials: CharacterRigMaterials): CharacterRig {
  const p = HUMANOID_PROPORTIONS;
  const modelRoot = new THREE.Group();

  const pelvis = new THREE.Mesh(new THREE.BoxGeometry(0.4, 0.22, 0.29), materials.dark);
  pelvis.position.y = p.hipY;
  modelRoot.add(pelvis);

  const torso = new THREE.Group();
  torso.position.y = p.torsoCenterY;
  const chest = new THREE.Mesh(new THREE.CylinderGeometry(0.31, 0.22, p.torsoHeight, 6), materials.body);
  chest.scale.z = 0.82;
  torso.add(chest);
  modelRoot.add(torso);

  const neck = new THREE.Mesh(new THREE.CylinderGeometry(0.1, 0.12, 0.28, 6), materials.body);
  neck.position.y = 2.34;
  modelRoot.add(neck);

  const head = new THREE.Group();
  head.position.y = p.headCenterY;
  const skull = new THREE.Mesh(new THREE.IcosahedronGeometry(p.headDiameter / 2, 0), materials.accent);
  skull.scale.set(0.86, 1, 0.9);
  head.add(skull);
  modelRoot.add(head);

  const leftArmChain = createJointedLimb(modelRoot, -p.shoulderX, p.shoulderY, materials.body, p.upperArmLength, p.lowerArmLength, 0.1, 0.075, "hand", materials.dark);
  const rightArmChain = createJointedLimb(modelRoot, p.shoulderX, p.shoulderY, materials.body, p.upperArmLength, p.lowerArmLength, 0.1, 0.075, "hand", materials.dark);
  const leftLegChain = createJointedLimb(modelRoot, -p.hipX, p.hipY, materials.dark, p.upperLegLength, p.lowerLegLength, 0.12, 0.095, "foot", materials.dark);
  const rightLegChain = createJointedLimb(modelRoot, p.hipX, p.hipY, materials.dark, p.upperLegLength, p.lowerLegLength, 0.12, 0.095, "foot", materials.dark);
  const { root: leftArm, mid: leftForearm, end: leftHand } = leftArmChain;
  const { root: rightArm, mid: rightForearm, end: rightHand } = rightArmChain;
  const { root: leftLeg, mid: leftShin, end: leftFoot } = leftLegChain;
  const { root: rightLeg, mid: rightShin, end: rightFoot } = rightLegChain;

  const weaponGrip = new THREE.Group();
  weaponGrip.name = "joint.weapon-grip";
  weaponGrip.position.set(0, -0.11, 0);
  weaponGrip.rotation.set(-0.16, 0, 0.08);
  rightHand.add(weaponGrip);
  const weaponSocket = socket("socket.weapon", weaponGrip, 0, -0.05, 0);
  const weapon = new THREE.Group();
  weaponSocket.add(weapon);
  const guard = new THREE.Group();
  guard.position.set(0, -0.06, 0.12);
  leftHand.add(guard);

  return {
    modelRoot,
    torso,
    head,
    leftArm,
    rightArm,
    leftForearm,
    rightForearm,
    leftHand,
    rightHand,
    leftLeg,
    rightLeg,
    leftShin,
    rightShin,
    leftFoot,
    rightFoot,
    weaponGrip,
    weapon,
    guard,
    sockets: {
      hud: socket("socket.hud", modelRoot, 0, 3.2, 0),
      weapon: weaponSocket,
      vfx: socket("socket.vfx", torso, 0, 0.1, 0.3),
      secondaryMotion: socket("socket.secondary", torso, 0, 0.3, -0.28),
    },
    materials,
    secondaryBodies: [],
    equippedItems: [],
    torsoBaseY: p.torsoCenterY,
    defaultGuardVisible: false,
  };
}

function createFallbackRig(entity: SampledEntity, materials: CharacterRigMaterials): CharacterRig {
  const rig = createHumanoidBase(materials);
  const baseItems = entity.archetype === "shieldbearer"
    ? ["armor.warrior.pauldrons", "offhand.kite-shield"]
    : entity.archetype === "ranger"
      ? []
      : ["weapon.arming-sword"];
  rig.equippedItems = equipHumanoid(rig, entity.appearance?.items ?? baseItems);
  rig.defaultGuardVisible = rig.equippedItems.includes("offhand.kite-shield");

  if (entity.archetype === "caster") {
    rig.weapon.scale.set(0.7, 1.2, 0.7);
    const cloak = new THREE.Mesh(new THREE.ConeGeometry(0.45, 1.12, 6, 1, true), materials.dark);
    cloak.position.y = 0.96;
    cloak.rotation.y = Math.PI / 6;
    rig.modelRoot.add(cloak);
    const focus = new THREE.Mesh(new THREE.OctahedronGeometry(0.15, 0), materials.accent);
    focus.position.set(0, 3.08, 0);
    focus.rotation.z = Math.PI / 4;
    rig.modelRoot.add(focus);
  } else if (entity.archetype === "ranger") {
    const hood = new THREE.Mesh(new THREE.ConeGeometry(0.27, 0.36, 6), materials.body);
    hood.position.y = 2.86;
    hood.rotation.y = Math.PI / 6;
    rig.modelRoot.add(hood);
    const bow = new THREE.Mesh(new THREE.TorusGeometry(0.34, 0.035, 4, 12, Math.PI * 1.55), materials.accent);
    bow.position.set(0, -0.62, 0.05);
    bow.rotation.z = -Math.PI * 0.76;
    rig.rightHand.add(bow);
  } else if (entity.archetype === "duelist") {
    rig.torso.scale.set(0.86, 1.03, 0.86);
    rig.weapon.scale.set(0.74, 1.2, 0.74);
    const offhand = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.72, 0.05), materials.accent);
    offhand.position.set(0, -0.6, 0);
    offhand.rotation.z = 0.12;
    rig.leftHand.add(offhand);
    const crest = new THREE.Mesh(new THREE.ConeGeometry(0.1, 0.45, 4), materials.accent);
    crest.position.set(0, 3.02, -0.04);
    crest.rotation.z = -0.18;
    rig.modelRoot.add(crest);
  }

  return rig;
}

function createCourtWarriorRig(entity: SampledEntity, materials: CharacterRigMaterials): CharacterRig {
  const rig = createHumanoidBase(materials);
  rig.modelRoot.name = COURT_WARRIOR_KIT_ID;
  rig.equippedItems = equipHumanoid(rig, entity.appearance?.items ?? COURT_WARRIOR_ITEMS);
  rig.defaultGuardVisible = rig.equippedItems.includes("offhand.kite-shield");
  return rig;
}

function poseCourtWarrior(rig: CharacterRig, context: CharacterPoseContext): void {
  resetRig(rig);
  const { entity, motion: sample, exaggeration, time } = context;

  if (sample.state === "defeated") {
    rig.modelRoot.rotation.z = -1.34;
    rig.modelRoot.rotation.y = 0.24;
    rig.modelRoot.position.y = 0.2;
    rig.leftArm.rotation.z = -0.45;
    rig.rightArm.rotation.z = 0.72;
    rig.leftForearm.rotation.x = -1.08;
    rig.rightForearm.rotation.x = -0.64;
    rig.leftShin.rotation.x = 0.74;
    rig.rightFoot.rotation.x = -0.38;
    rig.weaponGrip.rotation.z = -0.35;
    return;
  }

  if (sample.state === "hit") {
    poseImpactResponse(rig, context, true);
    return;
  }

  if (sample.state === "action") {
    poseAction(rig, context);
    return;
  }

  if (sample.state === "locomotion") {
    poseLocomotion(rig, context, true);
    return;
  }

  const breath = Math.sin(time * 2.1);
  rig.modelRoot.position.y = breath * 0.022;
  rig.torso.rotation.z = Math.sin(time * 1.15) * 0.025;
  rig.head.rotation.y = Math.sin(time * 0.72) * 0.11;
  rig.leftArm.rotation.x = -0.72 + breath * 0.035;
  rig.leftArm.rotation.z = -0.28;
  rig.leftForearm.rotation.x = -1.02;
  rig.leftHand.rotation.z = -0.12;
  rig.rightArm.rotation.x = 0.18 - breath * 0.025;
  rig.rightArm.rotation.z = -0.12;
  rig.rightForearm.rotation.x = -0.32 + breath * 0.025;
  rig.leftShin.rotation.x = 0.07;
  rig.rightShin.rotation.x = 0.07;
  const neutralRightHand = { x: 0.32, y: 1.28, z: -0.02 };
  const neutralLeftHand = { x: -0.63, y: 1.95, z: 0.77 };
  applyTwoBoneTarget(
    rig.rightArm,
    rig.rightForearm,
    neutralRightHand,
    HUMANOID_PROPORTIONS.upperArmLength,
    HUMANOID_PROPORTIONS.lowerArmLength,
    { x: 0, y: 0, z: 1 },
  );
  applyTwoBoneTarget(
    rig.leftArm,
    rig.leftForearm,
    neutralLeftHand,
    HUMANOID_PROPORTIONS.upperArmLength,
    HUMANOID_PROPORTIONS.lowerArmLength,
    { x: 0, y: 0, z: 1 },
  );
  rig.modelRoot.updateMatrixWorld(true);
  aimLocalChildAxis(rig.weaponGrip, rig.sockets.weapon, { x: 0.1, y: -0.18, z: 0.42 }, new THREE.Vector3(0, -1, 0), rig.modelRoot);
  aimLocalChildAxis(rig.guard, rig.guard, neutralLeftHand, new THREE.Vector3(1, 0, 0), rig.modelRoot);
}

const fallbackKit: ClassPresentationKit = {
  id: FALLBACK_KIT_ID,
  label: "Fallback actor",
  create: createFallbackRig,
  pose: poseFallback,
};

const courtWarriorKit: ClassPresentationKit = {
  id: COURT_WARRIOR_KIT_ID,
  label: "Court Warrior",
  create: createCourtWarriorRig,
  pose: poseCourtWarrior,
};

const kits = new Map<string, ClassPresentationKit>([
  [courtWarriorKit.id, courtWarriorKit],
]);

export function resolveClassPresentationKit(id?: string): ClassPresentationKit {
  return (id && kits.get(id)) || fallbackKit;
}
